
import {
  convertArrayToString,
  formatDateNormal,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { KulionerController } from "@/app/ui/controllers/KulionerController";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { PrintDataEntitas } from "@/domain/entities/Booking";
import { ModalMessageEntities } from "@/domain/entities/MainApp";
import { Options, prop, Vue } from "vue-class-component";
import generateThermalPDF from "@/app/ui/views/shipment/bulk-print-resi/modules/bulk-label-resi-thermal";
import JsBarcode from "jsbarcode";
import { ResponsePayload } from "@/domain/entities/ResponsePayload";
import generateKulionerBulkLabelResiThermal from "../modules/kulioner-bulk-label-resi-thermal";
import { KulionerMerchant, KulionerOrder } from "@/domain/entities/Kulioner";
import { KulionerListRequest } from "@/data/payload/api/KulionerApiRequest";

class Props {
  modelValue = prop<boolean>({
    default: false,
    type: Boolean,
    required: true
  });
  isMerchant = prop<boolean>({
    default: false,
    type: Boolean
  });
  data = prop<ResponsePayload[]>({
    default: () => [],
    type: Array
  });
  shipmentIds = prop<string[]>({
    default: () => [],
    type: Array
  });
  merchantData = prop<KulionerMerchant[]>({
    default: () => [],
    type: Array
  });
}

@Options({
  emits: ["update:modelValue"]
})
export default class ModalSuccessBulkProcessOrder extends Vue.with(Props) {
  get modalData(): ModalMessageEntities {
    const successAll = this.data.every((e) => e.success);

    return new ModalMessageEntities({
      title: successAll
        ? "Semua pesanan berhasil diproses untuk dikirim"
        : "Sebagian pesanan berhasil diproses untuk dikirim",
      image: successAll ? "ill_badge_success" : "badge-warning-lg",
      textCancel: "Tutup",
      onClose: () => {
        this.$emit("update:modelValue", false);
      },
      textSuccess: "Print thermal resi",
      onSubmit: () => {
        this.onPrintReceipt();
      }
    });
  }

  formatEstimation(estimation: string) {
    if (!estimation) return "-";

    const isMonthYearIsSame =
      formatDateNormal(estimation.split(" - ")[0], "MMMM YYYY") ===
      formatDateNormal(estimation.split(" - ")[1], "MMMM YYYY");
    const estimationDateStart = formatDateNormal(
      estimation.split(" - ")[0],
      `${isMonthYearIsSame ? "D" : "D MMMM YYYY"}`
    );
    const estimationDateEnd = formatDateNormal(
      estimation.split(" - ")[1],
      "D MMMM YYYY"
    );

    return `${estimationDateStart} - ${estimationDateEnd}`;
  }

  listStt: PrintDataEntitas[] = [];
  async getDetailStt() {
    try {
      const listStt = await KulionerController.getListStt({
        page: 1,
        limit: 50,
        destinationCityId: 0,
        userType: "",
        bookedBy: "",
        bookedType: "",
        fromDate: "",
        endDate: "",
        sttNumber: convertArrayToString(
          this.data.filter((e) => e.sttNumber),
          "sttNumber"
        ),
        isBulk: true
      });

      let listOrder:
        | PromiseSettledResult<KulionerOrder>[]
        | KulionerOrder[] = [];

      if (!this.isMerchant) {
        listOrder = await Promise.allSettled(
          this.data.map(
            async (data) =>
              await KulionerController.getKulionerDetail(data.data?.id)
          )
        );
      } else {
        const orderIdsRaw = await Promise.allSettled(
          this.shipmentIds.map(async (data) => {
            const resp = await KulionerController.getKulionerList(
              new KulionerListRequest({
                q: data,
                qType: "ORDER_ID",
                limit: 1
              })
            );
            return resp.data.orders[0].id;
          })
        );

        const orderIds = orderIdsRaw.filter(
          (ord) => ord.status === "fulfilled"
        ) as PromiseFulfilledResult<number>[];
        listOrder = await Promise.allSettled(
          orderIds.map(
            async (data) =>
              await KulionerController.getKulionerDetail(data.value)
          )
        );
      }

      const successData = listOrder.filter(
        (e) => e.status === "fulfilled"
      ) as PromiseFulfilledResult<KulionerOrder>[];

      this.listStt = successData
        .map((e) => e.value).filter((order) => order.orderSttNo)
        .map((order) => {
          const sttDetail = listStt.find(
            (stt) => stt.sttNo === order.orderSttNo
          );
          return new PrintDataEntitas({
            ...sttDetail,
            orderDetail: order
          });
        });
    } catch (err) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(err, "Gagal Print resi!")
      );
      throw err;
    }
  }

  async generateDataResi() {
    const list: PrintDataEntitas[] = [];

    await new Promise((resolve) => {
      this.listStt.filter(stt => stt.sttNo).forEach((item, index) => {
        if (item) {
          const barcode = document.createElement("img");
          const vendorBarcode = document.createElement("img");
          JsBarcode(barcode, item.sttNo, {
            textAlign: "left",
            displayValue: false,
            fontSize: 16
          });
          if (item.sttVendorRefferenceNo) {
            // vendor ref code
            JsBarcode(vendorBarcode, item.sttVendorRefferenceNo, {
              textAlign: "left",
              displayValue: false,
              fontSize: 16
            });
          }

          item.sttPieces.forEach((e) => {
            list.push(
              new PrintDataEntitas({
                ...e,
                ...item,
                barcode: barcode.src,
                vendorRefCode: vendorBarcode.src
              })
            );
          });

          barcode.remove();
          vendorBarcode.remove();
        }
        if (this.listStt.filter(stt => stt.sttNo).length - 1 === index) {
          resolve(true);
        }
      });
    });

    return list;
  }

  async printBulkReceipt() {
    const listBulkStt: PrintDataEntitas[] = await this.generateDataResi();
    if (!listBulkStt.length) return;

    new Promise(function(resolve) {
      MainAppController.showLoading();
      setTimeout(resolve.bind(null, null), 1);
    }).then(() => {
      const accountType = AccountController.accountData.account_type;
      generateKulionerBulkLabelResiThermal({
        data: listBulkStt,
        accountType
      });
    });
  }

  async onPrintReceipt() {
    try {
      MainAppController.showLoading();
      this.listStt = [];
      await this.getDetailStt();
      await this.printBulkReceipt();
    } finally {
      MainAppController.closeLoading();
    }
  }
}
